import { createSlice, createAction, createAsyncThunk } from '@reduxjs/toolkit';
import paymentJSService from '../../services/paymentJS';
import { placeOrder } from '../orders';
import {
  CARD_TOKEN_ERROR,
  PAYMENT_BLOCK,
  GIFT_CARD_ONLY_PAYMENT_ERROR,
} from '../../blocks/Checkout/SteppedCheckout/constants';
import { calculateTotalMinusGiftCards, convertToBuilderNotation, scrollToError } from '../../utils';
import storageUtils from '../../utils/storage';
import { savePaymentMethodData } from './index.migrated';

export const paymentMethods = {
  AFFIRM: 'AFFIRM',
  CREDIT_CARD: 'CLOVER',
  TRUEMED: 'TRUEMED',
  GIFT_CARD: 'GIFT_CARD',
  CHARGE_AFTER: 'CHARGE_AFTER',
  ACIMA: 'ACIMA',
  SHOP_PAY: 'SHOP_PAY',
  STRIPE: 'STRIPE',
};

const kountSessionIdFromLocalStorage = localStorage.getItem('kountSessionId');

const isStripeCustomPaymentConfiguration = window?.purTogglesEnabled?.(
  'stripe_custom_pmc',
);

export { savePaymentMethodData };

export const changePaymentMethod = createAction('payments/changePaymentMethod');

export const clearPaymentMethodsData = createAction(
  'payments/clearPaymentMethodsData',
  () => {
    storageUtils.cleanAllPaymentData();
    return {};
  },
);
export const paymentJSAuthorizeSessionSuccess = createAction(
  'payments/paymentJSAuthorizeSessionSuccess',
);

export const setDiscoverCardError = createAction(
  'payments/setDiscoverCardError',
);

export const setKountSessionId = createAction('payments/setKountSessionId');
export const setIsValidatingAddress = createAction(
  'payments/setIsValidatingAddress',
);
export const setAffirmFinancingProgramme = createAction(
  'payments/setAffirmFinancingProgramme',
);

export const initiateAffirmSteppedCheckout = createAsyncThunk(
  'payments/initiateAffirmCheckout',
  async (_, { dispatch, getState }) => {
    const {
      payments: { kountSessionId, paymentMethodData },
    } = getState();
    localStorage.setItem('kountSessionId', kountSessionId);

    dispatch(clearPaymentMethodsData());
    dispatch(savePaymentMethodData({}));
    dispatch(
      placeOrder({
        orderData: {
          paymentMethod: paymentMethods.AFFIRM,
          id: paymentMethodData.affirm.checkout_token,
          sessionId: kountSessionId,
        },
      }),
    );
  },
);

export const initiateChargeAfter = createAsyncThunk(
  'payments/initiateChargeAfter',
  async (_, { dispatch, getState }) => {
    const {
      payments: { kountSessionId, paymentMethodData },
    } = getState();
    localStorage.setItem('kountSessionId', kountSessionId);

    dispatch(clearPaymentMethodsData());
    dispatch(savePaymentMethodData({}));
    dispatch(
      placeOrder({
        orderData: {
          paymentMethod: paymentMethods.CHARGE_AFTER,
          id: paymentMethodData.chargeAfter.confirmationToken,
          sessionId: kountSessionId,
          chargeAfterLender: paymentMethodData.chargeAfter.lenderName,
        },
      }),
    );
  },
);

export const initiateCreditCardSteppedCheckout = createAsyncThunk(
  'payments/initiateCreditCardSteppedCheckout',
  async (_, { getState, dispatch }) => {
    const { paymentMethodData, kountSessionId } =
      getState().payments;

    dispatch(clearPaymentMethodsData());

    return dispatch(
      placeOrder({
        orderData: {
          paymentMethod: paymentMethods.STRIPE,
          ...paymentMethodData,
          storeKey: process.env.STRIPE_STORE_KEY ?? 'us-web',
          sessionId: kountSessionId,
        },
      }),
    );
  },
);

export const createStripeIntent = createAsyncThunk(
  'payments/createStripeIntent',
  async (_, { dispatch, getState }) => {
    try {
      const { taxedPrice, totalPrice, giftCards } = getState().cart;
      const { paymentMethodData } = getState().payments;
      const paymentMethodConfigurationId = process.env.STRIPE_AB_PAYMENT_METHOD_CONFIGURATION_ID;

      dispatch(clearPaymentMethodsData());
      dispatch(savePaymentMethodData());

      const totalWithGiftCards = giftCards && giftCards.length > 0 ?
        calculateTotalMinusGiftCards({taxedPrice, totalPrice, giftCards}) :
        taxedPrice.totalGross.centAmount;

      console.log('Stripe calling createPaymentIntent');
      const intentData = await paymentJSService.createPaymentIntent({
        centAmount: totalWithGiftCards,
        currency: taxedPrice.totalGross.currencyCode,
        storeKey: process.env.STRIPE_STORE_KEY ?? 'us-web',
        // Send a different payment method configuration ID if we are in a test.
        paymentMethodConfigurationId: isStripeCustomPaymentConfiguration ? paymentMethodConfigurationId : '',
      });

      console.log('Stripe intentData', intentData);
      dispatch(
        savePaymentMethodData({
          ...paymentMethodData,
          client_secret: intentData.client_secret,
        }),
      );
    } catch (error) {
      dispatch({
        type: 'addError',
        errorBlock: PAYMENT_BLOCK,
        errorCode: CARD_TOKEN_ERROR,
      });
      dispatch({ type: 'setPaymentStatus', status: 'failed' });
      scrollToError(PAYMENT_BLOCK);
    }
  },
);

export const initiateOnlyGiftCard = createAsyncThunk(
  'payments/initiateOnlyGiftCard',
  async (_, { getState, dispatch }) => {
    const { selectedPaymentMethod, kountSessionId } = getState().payments;
    try {
      dispatch(clearPaymentMethodsData());
      dispatch(savePaymentMethodData({}));
      dispatch(
        placeOrder({
          orderData: {
            paymentMethod: selectedPaymentMethod,
            id: selectedPaymentMethod,
            sessionId: kountSessionId,
          },
        }),
      );
    } catch (e) {
      dispatch({
        type: 'addError',
        errorBlock: PAYMENT_BLOCK,
        errorCode: GIFT_CARD_ONLY_PAYMENT_ERROR,
      });
      scrollToError(PAYMENT_BLOCK);
    }
  },
);

export const initiateAcimaSteppedCheckout = createAsyncThunk(
  'payments/initiateAcimaSteppedCheckout',
  async (_, { getState, dispatch }) => {
    const { paymentMethodData, selectedPaymentMethod, kountSessionId } =
      getState().payments;

    dispatch(clearPaymentMethodsData());
    dispatch(
      placeOrder({
        orderData: {
          paymentMethod: selectedPaymentMethod,
          id: paymentMethodData.acima.leaseId,
          token: paymentMethodData.acima.checkoutToken,
          sessionId: kountSessionId,
        },
      }),
    );
  },
);

export const initiateTruemedSteppedCheckout = createAsyncThunk(
  'payments/initiateTruemedSteppedCheckout',
  async (_, { getState, dispatch }) => {
    const { paymentMethodData, selectedPaymentMethod, kountSessionId } =
      getState().payments;

    dispatch(clearPaymentMethodsData());
    dispatch(
      placeOrder({
        orderData: {
          paymentMethod: selectedPaymentMethod,
          id: paymentMethodData.truemed.id,
          token: paymentMethodData.truemed.token,
          sessionId: kountSessionId,
        },
      }),
    );
  },
);

export const completeOrderSteppedCheckout = createAsyncThunk(
  'order/completeOrderSteppedCheckout',
  async (_, { getState, dispatch }) => {
    const { selectedPaymentMethod } = getState().payments;

    switch (selectedPaymentMethod) {
      case paymentMethods.CREDIT_CARD:
        return dispatch(initiateCreditCardSteppedCheckout());
      case paymentMethods.AFFIRM:
        return dispatch(initiateAffirmSteppedCheckout());
      case paymentMethods.GIFT_CARD:
        return dispatch(initiateOnlyGiftCard());
      case paymentMethods.CHARGE_AFTER:
        return dispatch(initiateChargeAfter());
      case paymentMethods.ACIMA:
        return dispatch(initiateAcimaSteppedCheckout());
      case paymentMethods.TRUEMED:
        return dispatch(initiateTruemedSteppedCheckout());
      default:
        // eslint-disable-next-line no-console
        console.log('Payment method not implemented.');
    }
    return null;
  },
);

export const paymentJSAuthorizeSession = createAsyncThunk(
  'payments/paymentJSAuthorizeSession',
  async (callback, token, { getState }) => {
    const { id, isDraft } = getState().cart;

    return paymentJSService.authorizeSession(id, isDraft, token, callback);
  },
);

const getInitialPaymentMethod = () => paymentMethods.CREDIT_CARD;

export const paymentsSlice = createSlice({
  name: 'payments',
  initialState: {
    status: 'idle',
    errorCode: undefined,
    selectedPaymentMethod: getInitialPaymentMethod(),
    paymentMethodData: undefined,
    paymentJSClientToken: null,
    validatingFields: false,
    kountSessionId: kountSessionIdFromLocalStorage,
    affirmFinancingProgramme: null,
    discoverCardError: false,
    paymentToken: null,
  },
  reducers: {
    changePaymentMethod: (state, action) => ({
      ...state,
      selectedPaymentMethod: action.payload,
    }),
    clearPaymentMethodsData: (state) => ({
      ...state,
      paymentJSClientToken: undefined,
    }),
    paymentJSAuthorizeSessionSuccess: (state, action) => ({
      ...state,
      paymentJSClientToken: action.payload,
    }),
    setKountSessionId: (state, action) => ({
      ...state,
      kountSessionId: action.payload,
    }),
    savePaymentMethodData: (state, action) => ({
      ...state,
      paymentMethodData: action.payload,
    }),
    setDiscoverCardError: (state, action) => ({
      ...state,
      discoverCardError: action.payload,
    }),
  },
  extraReducers: convertToBuilderNotation({
    [completeOrderSteppedCheckout.pending]: (state) => ({
      ...state,
      status: 'loading',
      validatingFields: true,
    }),
    [completeOrderSteppedCheckout.fulfilled]: (state) => ({
      ...state,
      status: 'succeeded',
      validatingFields: false,
    }),
    [completeOrderSteppedCheckout.rejected]: (state) => ({
      ...state,
      status: 'failed',
      validatingFields: false,
    }),
    [createStripeIntent.pending]: (state) => ({
      ...state,
      status: 'loading',
    }),
    [createStripeIntent.fulfilled]: (state) => ({
      ...state,
      status: 'succeeded',
    }),
    [createStripeIntent.rejected]: (state) => ({
      ...state,
      status: 'failed',
    }),
    setPaymentStatus: (state, payload) => ({
      ...state,
      status: payload.status,
    }),
    setPaymentToken: (state, payload) => ({
      ...state,
      paymentToken: payload.token,
    }),
    setIsValidatingAddress: (state, payload) => ({
      ...state,
      validatingFields: payload.isValidatingAddress,
    }),
    setAffirmFinancingProgramme: (state, payload) => ({
      ...state,
      affirmFinancingProgramme: payload.affirmFinancingProgramme,
    }),
  }),
});

export default paymentsSlice.reducer;
